import React, { useState, useEffect, useRef } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import Cookies from 'js-cookie';
import './App.css';
import HomePage from './components/HomePage';
import LobbyPage from './components/LobbyPage';
import GamePage from './components/GamePage';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.imposterfree.com';
axios.defaults.withCredentials = true;

function App() {
  const [socket, setSocket] = useState(null);
  const [page, setPage] = useState('home'); // home, lobby, game
  const [roomCode, setRoomCode] = useState('');
  const [playerName, setPlayerName] = useState('');
  const [isHost, setIsHost] = useState(false);
  const [roomData, setRoomData] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [isCheckingToken, setIsCheckingToken] = useState(false);
  
  // Ref to store latest state for visibility handler
  const stateRef = useRef({ page, roomCode, socket });
  useEffect(() => {
    stateRef.current = { page, roomCode, socket };
  }, [page, roomCode, socket]);

  useEffect(() => {
    // Check server health first
    axios.get(`${API_URL}/api/health`, {
      withCredentials: true,
      timeout: 5000
    })
      .then(response => {
        console.log('Server health check:', response.data);
      })
      .catch(err => {
        console.error('Server health check failed:', err);
        setError(`Cannot connect to server at ${API_URL}. Please make sure the server is running.`);
      });
    
    // Load categories
    axios.get(`${API_URL}/api/categories`, {
      withCredentials: true,
      timeout: 10000
    })
      .then(response => {
        setCategories(response.data.categories);
      })
      .catch(err => {
        console.error('Error loading categories:', err);
        if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
          setError(`Cannot connect to server at ${API_URL}. Please make sure the server is running.`);
        }
      });

    // Check for existing token and auto-rejoin on page load/refresh
    const token = Cookies.get('roomToken');
    if (token) {
      setIsCheckingToken(true);
      // Small delay to ensure page state is initialized
      const timeoutId = setTimeout(() => {
        attemptRejoin(token);
      }, 300);
      
      return () => clearTimeout(timeoutId);
    }
  }, []); // Empty deps - only run once on mount

  // Handle page visibility changes - reconnect when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const token = Cookies.get('roomToken');
        const currentState = stateRef.current;
        
        // Only reconnect if we have a token and are in a room/game
        if (token && (currentState.page === 'lobby' || currentState.page === 'game' || currentState.roomCode)) {
          console.log('Page became visible, refreshing game state...');
          
          // Always do a full rejoin via API to get the latest state
          // This ensures we get the most up-to-date game state (including if it was revealed)
          try {
            await attemptRejoin(token);
            console.log('State refreshed successfully after visibility change');
          } catch (err) {
            console.error('Rejoin failed on visibility change:', err);
            // If API rejoin fails, try socket rejoin as fallback
            if (currentState.socket && currentState.socket.connected) {
              console.log('Falling back to socket rejoin...');
              currentState.socket.emit('rejoin-with-token', { token });
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also handle focus event as backup
    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        handleVisibilityChange();
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [socket, page, roomCode]); // Re-run when socket, page, or roomCode changes

  const attemptRejoin = async (token) => {
    try {
      console.log('Attempting to rejoin with token...');
      
      // Disconnect existing socket if any
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }

      const response = await axios.post(`${API_URL}/api/room/rejoin`, { token }, {
        withCredentials: true // Include cookies in request
      });
      const { room, playerId, playerName, isHost: isHostFromServer, activeGame } = response.data;
      
      console.log('Rejoin successful:', { roomCode: room.code, playerName, isHost: isHostFromServer, hasActiveGame: !!activeGame });
      
      setRoomCode(room.code);
      setPlayerName(playerName);
      setIsHost(isHostFromServer);
      setRoomData(room);
      
      // If there's an active game, set game data
      if (activeGame) {
        // Preserve existing vote data if available
        setGameData(prev => ({
          ...activeGame,
          playerName: playerName, // Include player name for vote tracking
          voteCounts: activeGame.voteCounts || prev?.voteCounts || {},
          votes: activeGame.votes || prev?.votes || {},
          voterToTarget: activeGame.voterToTarget || prev?.voterToTarget || {},
          allPlayers: activeGame.allPlayers || prev?.allPlayers || []
        }));
        setPage('game');
      } else {
        setPage('lobby');
        setGameData(null);
      }
      
      // Connect socket
      const newSocket = io(API_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity
      });
      setSocket(newSocket);
      
      setupSocketListeners(newSocket);
      
      // If already connected, emit immediately, otherwise wait
      if (newSocket.connected) {
        console.log('Socket already connected:', newSocket.id);
        newSocket.emit('rejoin-with-token', { token });
      } else {
        // Wait for connection before emitting
        newSocket.once('connect', () => {
          console.log('Socket connected:', newSocket.id);
          newSocket.emit('rejoin-with-token', { token });
        });
      }
      
      // Rejoin successful, hide loading screen
      setIsCheckingToken(false);
    } catch (err) {
      // Token invalid or room doesn't exist, clear cookie
      console.log('Could not rejoin room:', err.response?.data?.error || err.message);
      Cookies.remove('roomToken');
      // Hide loading screen and show home page
      setIsCheckingToken(false);
      // Don't show error to user - just let them create/join normally
    }
  };

  const createRoom = async (name) => {
    try {
      // Don't clear token - let it be set when creating
      // This allows refresh to work
      
      // Disconnect existing socket if any
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }

      console.log('Creating room, API_URL:', API_URL);
      const response = await axios.post(`${API_URL}/api/room/create`, {}, {
        withCredentials: true, // Include cookies in request
        timeout: 10000 // 10 second timeout
      }).catch(err => {
        console.error('Create room error:', err);
        if (err.code === 'ECONNREFUSED' || err.message.includes('Network Error')) {
          throw new Error('Cannot connect to server. Please make sure the server is running on ' + API_URL);
        }
        throw err;
      });
      
      const { code } = response.data;
      
      setPlayerName(name);
      setRoomCode(code);
      setIsHost(true);
      
      // Connect socket
      const newSocket = io(API_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true
      });
      setSocket(newSocket);
      
      setupSocketListeners(newSocket);
      
      // If already connected, emit immediately, otherwise wait
      if (newSocket.connected) {
        console.log('Socket already connected:', newSocket.id);
        newSocket.emit('join-room', { code, playerName: name });
      } else {
        // Wait for connection before emitting
        newSocket.once('connect', () => {
          console.log('Socket connected:', newSocket.id);
          newSocket.emit('join-room', { code, playerName: name });
        });
      }
      
      setPage('lobby');
    } catch (err) {
      console.error('Create room failed:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to create room';
      setError(errorMessage);
      
      // If it's a network error, provide helpful message
      if (errorMessage.includes('Network Error') || errorMessage.includes('ECONNREFUSED')) {
        setError(`Cannot connect to server at ${API_URL}. Please make sure the server is running.`);
      }
    }
  };

  const joinRoom = (code, name) => {
    try {
      // Clear any previous errors
      setError('');
      
      // Don't clear token - let it be set when joining
      // This allows refresh to work
      
      // Disconnect existing socket if any
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }

      setPlayerName(name);
      setRoomCode(code);
      setIsHost(false);
      
      // Connect socket
      const newSocket = io(API_URL, {
        transports: ['websocket', 'polling'],
        reconnection: true
      });
      setSocket(newSocket);
      
      setupSocketListeners(newSocket);
      
      // Set up error handler BEFORE emitting join-room
      const errorHandler = (errorData) => {
        console.error('Join room error:', errorData);
        setError(errorData.message || 'Failed to join room. Please check the room code and try again.');
        // Return to home page on error
        setPage('home');
        setRoomCode('');
        setRoomData(null);
        // Disconnect socket on error
        if (newSocket) {
          newSocket.removeAllListeners();
          newSocket.disconnect();
          setSocket(null);
        }
      };
      
      // Set up success handler
      const successHandler = () => {
        // Only navigate to lobby when we successfully join
        setPage('lobby');
        // Remove the one-time error listener since we succeeded
        newSocket.off('room-error', errorHandler);
      };
      
      // Listen for errors (one-time, will be removed on success)
      newSocket.once('room-error', errorHandler);
      
      // Listen for successful join
      newSocket.once('room-joined', successHandler);
      
      // If already connected, emit immediately, otherwise wait
      if (newSocket.connected) {
        console.log('Socket already connected:', newSocket.id);
        newSocket.emit('join-room', { code, playerName: name });
      } else {
        // Wait for connection before emitting
        newSocket.once('connect', () => {
          console.log('Socket connected:', newSocket.id);
          newSocket.emit('join-room', { code, playerName: name });
        });
        
        // Also handle connection errors
        newSocket.once('connect_error', (err) => {
          console.error('Connection error:', err);
          setError('Failed to connect to server. Please check your connection and try again.');
          setPage('home');
          setSocket(null);
        });
      }
    } catch (err) {
      console.error('Join room failed:', err);
      setError('Failed to join room. Please try again.');
      setPage('home');
    }
  };

  const setupSocketListeners = (socket) => {
    // Remove old listeners to prevent duplicates
    socket.removeAllListeners();
    
    socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    socket.on('connect', () => {
      console.log('Socket connected:', socket.id);
      // If we have a token and are in a room, rejoin automatically
      // Use a small delay to ensure state is ready
      setTimeout(() => {
        const token = Cookies.get('roomToken');
        const currentPage = page;
        const currentRoomCode = roomCode;
        if (token && (currentPage === 'lobby' || currentPage === 'game' || currentRoomCode)) {
          console.log('Socket reconnected, rejoining room...');
          socket.emit('rejoin-with-token', { token });
        }
      }, 100);
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setError('Connection error. Please refresh the page.');
    });
    
    socket.on('set-token', (data) => {
      // Store token in cookie (always store to ensure persistence)
      if (data.token) {
        Cookies.set('roomToken', data.token, { expires: 7, sameSite: 'lax' }); // 7 days
        console.log('Token stored via set-token event');
      }
    });

    socket.on('room-joined', (data) => {
      console.log('Room joined:', data);
      // Clear any errors on successful join
      setError('');
      
      // Ensure imposterCount is set (default to 1 if not present)
      const roomDataWithDefaults = {
        ...data,
        imposterCount: data.imposterCount || 1
      };
      setRoomData(roomDataWithDefaults);
      // Update isHost - use explicit isHost from server if provided, otherwise check hostId
      const isHostStatus = data.isHost !== undefined ? data.isHost : (data.hostId === socket.id);
      setIsHost(isHostStatus);
      console.log('Setting isHost:', isHostStatus, 'from data.isHost:', data.isHost, 'hostId:', data.hostId, 'socket.id:', socket.id);
      
      // Store token if provided (always store to ensure persistence)
      if (data.token) {
        Cookies.set('roomToken', data.token, { expires: 7, sameSite: 'lax' });
        console.log('Token stored in cookie via room-joined');
      }
      
      // If there's an active game, navigate to game page and set game data
      if (data.activeGame) {
        console.log('Active game found in room-joined, setting game data:', data.activeGame);
        const updatedGameData = {
          ...data.activeGame,
          playerName: playerName, // Include player name for vote tracking
          voteCounts: data.activeGame.voteCounts || gameData?.voteCounts || {},
          votes: data.activeGame.votes || gameData?.votes || {},
          voterToTarget: data.activeGame.voterToTarget || gameData?.voterToTarget || {},
          allPlayers: data.activeGame.allPlayers || gameData?.allPlayers || []
        };
        setGameData(updatedGameData);
        
        // Always navigate to game page if there's an active game
        // This ensures we show the game page even if we were on a different page
        setPage('game');
        
        // If game is revealed, make sure the revealed state is set
        if (data.activeGame.revealed) {
          console.log('Game is revealed, updating revealed state');
          setGameData(prev => ({
            ...prev,
            revealed: data.activeGame.revealed,
            // Preserve vote data when updating revealed state
            voteCounts: prev?.voteCounts || data.activeGame.voteCounts || {},
            votes: prev?.votes || data.activeGame.votes || {},
            voterToTarget: prev?.voterToTarget || data.activeGame.voterToTarget || {},
            allPlayers: prev?.allPlayers || data.activeGame.allPlayers || []
          }));
        }
      } else {
        // No active game - go to lobby
        console.log('No active game in room-joined, going to lobby');
        setPage('lobby');
        setGameData(null);
      }
    });

    socket.on('room-error', (data) => {
      console.error('Room error received:', data);
      setError(data.message || 'An error occurred');
      // If we're on lobby or game page and get an error, go back to home
      if (page === 'lobby' || page === 'game') {
        setPage('home');
        setRoomData(null);
        setGameData(null);
        setRoomCode('');
      }
    });

    socket.on('player-list-updated', (data) => {
      console.log('Player list updated:', data);
      setRoomData(prev => {
        if (prev && data.players) {
          return { 
            ...prev, 
            players: data.players, 
            playerCount: data.players.length 
          };
        }
        return prev;
      });
    });

    socket.on('player-joined', (data) => {
      console.log('Player joined:', data);
      // This will trigger player-list-updated, but we can also update here
      setRoomData(prev => {
        if (prev && data.player) {
          // Check if player already exists
          const playerExists = prev.players?.some(p => p.id === data.player.id);
          if (!playerExists) {
            return {
              ...prev,
              players: [...(prev.players || []), data.player],
              playerCount: (prev.players?.length || 0) + 1
            };
          }
        }
        return prev;
      });
    });

    socket.on('category-selected', (data) => {
      console.log('Category selected:', data);
      setRoomData(prev => {
        if (prev && data.category) {
          // Save to localStorage when category is selected
          localStorage.setItem('imposter_game_category', JSON.stringify(data.category));
          return { ...prev, selectedCategory: data.category };
        }
        return prev;
      });
    });

    socket.on('imposter-count-set', (data) => {
      setRoomData(prev => {
        if (prev) {
          return { ...prev, imposterCount: data.count };
        }
        return prev;
      });
    });

    socket.on('hints-toggled', (data) => {
      // This is just for other players to see the setting
      // The actual state is managed locally for the host
    });

    socket.on('game-started', (data) => {
      console.log('Game started, received data:', data);
      setGameData(prev => ({
        ...data,
        playerName: playerName, // Include player name for vote tracking
        voteCounts: data.voteCounts || prev?.voteCounts || {}, // Preserve existing vote counts
        votes: data.votes || prev?.votes || {}, // Preserve existing votes
        voterToTarget: data.voterToTarget || prev?.voterToTarget || {}, // Preserve voterToTarget
        allPlayers: data.allPlayers || prev?.allPlayers || [] // Ensure allPlayers is set
      }));
      setPage('game');
      // Don't clear token - user should be able to rejoin game
      // Make sure we stay on game page even if room-joined fires after
    });

    socket.on('game-started-broadcast', (data) => {
      console.log('Game started broadcast:', data);
      // Update game data with starting player info, preserving all vote data
      if (data.startingPlayer) {
        setGameData(prev => ({
          ...prev,
          startingPlayer: data.startingPlayer,
          // Preserve vote data
          voteCounts: prev?.voteCounts || {},
          votes: prev?.votes || {},
          voterToTarget: prev?.voterToTarget || {},
          allPlayers: prev?.allPlayers || []
        }));
      }
    });

    socket.on('game-revealed', (data) => {
      setGameData(prev => ({
        ...prev,
        revealed: data,
        // Update voting data if included in reveal
        voteCounts: data.voteCounts || prev?.voteCounts || {},
        votes: data.votes || prev?.votes || {},
        voterToTarget: data.voterToTarget || prev?.voterToTarget || {}
      }));
    });

    socket.on('active-game-rejoined', (data) => {
      console.log('Active game rejoined:', data);
      setGameData(prev => ({
        ...data,
        playerName: playerName, // Include player name for vote tracking
        voteCounts: data.voteCounts || prev?.voteCounts || {}, // Preserve existing vote counts
        votes: data.votes || prev?.votes || {}, // Preserve existing votes
        voterToTarget: data.voterToTarget || prev?.voterToTarget || {}, // Preserve voterToTarget
        allPlayers: data.allPlayers || prev?.allPlayers || [] // Ensure allPlayers is set
      }));
      setPage('game');
    });

    socket.on('player-left', (data) => {
      console.log('Player left:', data);
      // Update room data to reflect player leaving
      setRoomData(prev => {
        if (prev && prev.players) {
          return {
            ...prev,
            players: prev.players.filter(p => p.id !== data.playerId)
          };
        }
        return prev;
      });
      
      // If host changed, update host status
      if (data.wasHost) {
        setIsHost(false);
      }
    });

    socket.on('player-removed', (data) => {
      console.log('Player removed:', data);
      setError(data.message || 'You have been removed from the room by the host');
      // Navigate back to home after showing error
      setTimeout(() => {
        disconnect();
      }, 2000);
    });

    socket.on('host-changed', (data) => {
      console.log('Host changed:', data);
      setRoomData(prev => {
        if (prev) {
          return { ...prev, hostId: data.newHostId };
        }
        return prev;
      });
      // Check if current user is now the host
      if (socket && socket.id === data.newHostId) {
        setIsHost(true);
      }
    });

    socket.on('player-left-game', (data) => {
      console.log('Player left game:', data);
      // Update game data if needed
      if (data.gameContinues) {
        // Game continues - just log it
        console.log(`Game continues with ${data.remainingPlayers} player(s)`);
      }
    });

    socket.on('game-ended', (data) => {
      console.log('Game ended:', data);
      // Clear game data and return to lobby
      setGameData(null);
      setPage('lobby');
      setRoomData(prev => {
        if (prev) {
          return { ...prev, state: 'LOBBY' };
        }
        return prev;
      });
    });

    socket.on('new-game-ready', () => {
      setGameData(null);
      setRoomData(prev => {
        if (prev) {
          // Load saved settings from localStorage and restore them
          const savedCategory = localStorage.getItem('imposter_game_category');
          const savedImposterCount = localStorage.getItem('imposter_game_imposter_count');
          
          const updated = { ...prev, state: 'LOBBY' };
          
          // Restore category
          if (savedCategory) {
            try {
              const category = JSON.parse(savedCategory);
              updated.selectedCategory = category;
            } catch (e) {
              console.error('Error parsing saved category:', e);
            }
          }
          
          // Restore imposter count
          if (savedImposterCount) {
            updated.imposterCount = parseInt(savedImposterCount, 10);
          }
          
          return updated;
        }
        return prev;
      });
    });

    socket.on('game-data-update', (data) => {
      console.log('Game data update received:', data);
      // This is the unified game data event that includes voting status
      setGameData(prev => ({
        ...prev,
        ...data,
        playerName: playerName, // Preserve player name
        // Ensure vote data is always included
        voteCounts: data.voteCounts || prev?.voteCounts || {},
        votes: data.votes || prev?.votes || {},
        voterToTarget: data.voterToTarget || prev?.voterToTarget || {},
        allPlayers: data.allPlayers || prev?.allPlayers || []
      }));
    });

    socket.on('votes-updated', (data) => {
      console.log('Votes updated:', data);
      // Keep this for backward compatibility, but game-data-update is preferred
      setGameData(prev => ({
        ...prev,
        voteCounts: data.voteCounts || {},
        votes: data.votes || {}, // Store full votes object to track current user's vote
        voterToTarget: data.voterToTarget || {} // Store who each voter voted for
      }));
    });

    socket.on('player-disconnected', () => {
      // Player left, update list will come
    });
  };

  const handleSelectCategory = (categoryId) => {
    if (socket && isHost) {
      socket.emit('select-category', { categoryId });
      // Category will be saved in the category-selected event handler
    }
  };

  const handleSetImposterCount = (count) => {
    if (socket && isHost) {
      socket.emit('set-imposter-count', { count });
      // Save to localStorage immediately
      localStorage.setItem('imposter_game_imposter_count', count.toString());
    }
  };

  const handleStartGame = (hintsEnabled = false) => {
    if (socket && isHost && roomData) {
      // Use imposterCount from roomData, default to 1 if not set
      const imposterCount = roomData.imposterCount || 1;
      socket.emit('start-game', {
        categoryId: roomData.selectedCategory?.id,
        imposterCount: imposterCount,
        hintsEnabled: hintsEnabled
      });
    }
  };

  const handleToggleHints = (enabled) => {
    if (socket && isHost) {
      socket.emit('toggle-hints', { enabled });
    }
  };

  const handleRevealGame = () => {
    if (socket && isHost) {
      socket.emit('reveal-game');
    }
  };

  const handleNewGame = () => {
    if (socket && isHost) {
      socket.emit('new-game');
    }
  };

  const handleSitOut = (playerId = null) => {
    if (socket) {
      socket.emit('sit-out', playerId ? { playerId } : {});
    }
  };

  const handleJoinGame = (playerId = null) => {
    if (socket) {
      socket.emit('join-game', playerId ? { playerId } : {});
    }
  };

  const handleRemovePlayer = (playerId) => {
    if (socket && isHost) {
      socket.emit('remove-player', { playerId });
    }
  };

  const handleUpdateName = (name) => {
    if (socket) {
      socket.emit('update-name', { name });
      setPlayerName(name);
    }
  };

  const disconnect = () => {
    if (socket) {
      // Emit leave-room event to properly remove player from room and game
      socket.emit('leave-room');
      // Wait a moment for server to process, then disconnect
      setTimeout(() => {
        socket.removeAllListeners();
        socket.disconnect();
        setSocket(null);
      }, 100);
    }
    // Clear token when manually disconnecting
    Cookies.remove('roomToken');
    setPage('home');
    setRoomCode('');
    setRoomData(null);
    setGameData(null);
    setIsHost(false);
    setError('');
  };

  return (
    <div className="App">
      {isCheckingToken ? (
        <div className="loading-screen">
          <div className="loading-content">
            <div className="imposter-animation">
              <div className="imposter-character">
                <div className="imposter-head">
                  <div className="imposter-eyes">
                    <div className="eye"></div>
                    <div className="eye"></div>
                  </div>
                </div>
              </div>
            </div>
            <p className="loading-text">Checking for active game...</p>
          </div>
        </div>
      ) : (
        <>
          {page === 'home' && (
            <HomePage
              onCreateRoom={createRoom}
              onJoinRoom={joinRoom}
              error={error}
            />
          )}
          {page === 'lobby' && roomData && (
            <LobbyPage
              roomData={roomData}
              isHost={isHost}
              playerName={playerName}
              categories={categories}
              socket={socket}
              onSelectCategory={handleSelectCategory}
              onSetImposterCount={handleSetImposterCount}
              onStartGame={handleStartGame}
              onToggleHints={handleToggleHints}
              onSitOut={handleSitOut}
              onJoinGame={handleJoinGame}
              onRemovePlayer={handleRemovePlayer}
              onUpdateName={handleUpdateName}
              onDisconnect={disconnect}
              error={error}
            />
          )}
          {page === 'game' && gameData && (
            <GamePage
              gameData={gameData}
              isHost={isHost}
              socket={socket}
              playerName={playerName}
              onRevealGame={handleRevealGame}
              onNewGame={handleNewGame}
              onDisconnect={disconnect}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;

